import i18next from 'i18next';
import React, { useCallback, useEffect, useState } from 'react';

import { DEFAULT_NS, I18nOptions, i18nTrans as _i18nextT, getCurrentLanguage, normalizeArgs } from './instance';
import { ELanguage } from './types';

export type I18nTFunc = (key: string, ns?: string | I18nOptions, options?: I18nOptions) => string;

export interface IUseLocaleReturn {
  /**
   * 翻译函数。
   *
   * 查找顺序：targetNs → common（如果 targetNs !== common）
   *
   * @param key  翻译 key（通常即英文文案本身）
   * @param ns   覆盖默认 ns。不传时使用 useLocale(defaultNs) 传入的 ns。
   *             也允许 `t(key, options)` 这种 legacy 用法（第二个参数是 options 对象）。
   * @param options 插值参数
   *
   * @example
   *   const { t } = useLocale('apps');
   *   t('Save')                       // -> 先查 apps.Save，没有再查 common.Save
   *   t('Edit app')                   // -> apps['Edit app']
   *   t('xxx', 'performance')         // -> 覆盖：先查 performance.xxx，再查 common.xxx
   *   t('Joined on {{date}}', { date: '...' })  // legacy: options 当第二参
   */
  t: I18nTFunc
  Trans: React.FC<{ k: string; ns?: string; options?: I18nOptions }>;
  lang: string;
}

/**
 * @param defaultNs 默认 namespace，不传时为 'common'。
 *   t() 查找顺序：targetNs → common（fallback）。
 */
export function useLocale(defaultNs?: string): IUseLocaleReturn {
  const [lang, setLang] = useState(() => getCurrentLanguage());
  const baseNs = defaultNs || DEFAULT_NS;

  useEffect(() => {
    const handler = (lng: string) => setLang(lng as ELanguage);
    i18next.on('languageChanged', handler);
    return () => {
      i18next.off('languageChanged', handler);
    };
  }, []);

  const t = useCallback((key: string, ns?: string | I18nOptions, options?: I18nOptions) => {
    const { ns: overrideNs, options: realOptions } = normalizeArgs(ns, options);

    // 确定实际 ns：t() 调用时显式传的 > useLocale() 传入的 > common
    const targetNs = overrideNs !== DEFAULT_NS ? overrideNs : baseNs;

    // i18next 原生 fallbackNS 会自动 fallback 到 common
    return _i18nextT(key, targetNs, realOptions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang, baseNs]);

  const Trans: React.FC<{ k: string; ns?: string; options?: I18nOptions }> = useCallback(
    ({ k, ns, options }) => React.createElement(React.Fragment, null, t(k, ns, options)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lang, baseNs],
  );

  return { t, Trans, lang };
}
